// src/services/yearlyGraphService.js
// Service to manage consolidated yearly_graph_summary document for faster data loading

import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs, query, orderBy } from "firebase/firestore";
import { DATABASE } from "../Config/firebase";

const CONSOLIDATED_DOC_PATH = "global_data/yearly_graph_summary";

/**
 * Deduplicate yearly_graph entries by month/year, keeping latest by created_at
 * Same logic as YearlyGraph.jsx (lines 64-86)
 */
const deduplicateEntries = (entries) => {
  const monthlyMap = new Map();
  
  entries.forEach(entry => {
    const key = `${entry.month}-${entry.year}`;
    if (!monthlyMap.has(key)) {
      monthlyMap.set(key, entry);
    } else {
      // Replace with newer entry if this one is more recent
      const existing = monthlyMap.get(key);
      const existingDate = existing?.created_at?.toDate 
        ? existing.created_at.toDate() 
        : existing?.created_at instanceof Date 
          ? existing.created_at 
          : new Date(0);
      const currentDate = entry?.created_at?.toDate 
        ? entry.created_at.toDate() 
        : entry?.created_at instanceof Date 
          ? entry.created_at 
          : new Date(0);
      
      if (currentDate > existingDate) {
        monthlyMap.set(key, entry);
      }
    }
  });
  
  // Convert back to array and sort chronologically
  return Array.from(monthlyMap.values()).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
};

/**
 * Recalculate point field for all months in consolidated document
 * Point = cumulative sum of all previous months' amount values + current month's amount
 */
const recalculateAllPoints = (months) => {
  let cumulativePoint = 0;
  
  return months.map(month => {
    cumulativePoint += month.amount || 0;
    return {
      ...month,
      point: cumulativePoint
    };
  });
};

/**
 * Get yearly graph summary from consolidated document
 * Falls back to querying collection if consolidated document doesn't exist
 */
export const getYearlyGraphSummary = async () => {
  try {
    // Try to read from consolidated document first
    const summaryDocRef = doc(DATABASE, CONSOLIDATED_DOC_PATH);
    const summaryDocSnap = await getDoc(summaryDocRef);
    
    if (summaryDocSnap.exists()) {
      const data = summaryDocSnap.data();
      console.log("✅ Loaded from consolidated document:", data.months?.length, "months");
      return {
        months: data.months || [],
        lastUpdated: data.lastUpdated,
        version: data.version || 1,
        source: "consolidated"
      };
    }
    
    // Fallback: Query collection and apply deduplication
    console.log("⚠️ Consolidated document not found, querying collection...");
    const q = query(collection(DATABASE, "yearly_graph"), orderBy("created_at", "desc"));
    const querySnapshot = await getDocs(q);
    
    let entries = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Apply deduplication (same logic as YearlyGraph.jsx)
    entries = deduplicateEntries(entries);
    
    // Sort chronologically
    entries.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
    
    console.log("✅ Loaded from collection with deduplication:", entries.length, "months");
    return {
      months: entries,
      lastUpdated: null,
      version: 1,
      source: "collection"
    };
  } catch (error) {
    console.error("❌ Error loading yearly graph summary:", error);
    throw error;
  }
};

/**
 * Update consolidated document with new month data
 * Recalculates point field for all months
 */
export const updateYearlyGraphSummary = async (monthData) => {
  try {
    const summaryDocRef = doc(DATABASE, CONSOLIDATED_DOC_PATH);
    const summaryDocSnap = await getDoc(summaryDocRef);
    
    let existingMonths = [];
    if (summaryDocSnap.exists()) {
      existingMonths = summaryDocSnap.data().months || [];
    }
    
    // Find if month already exists
    const monthIndex = existingMonths.findIndex(
      m => m.year === monthData.year && m.month === monthData.month
    );
    
    if (monthIndex >= 0) {
      // Update existing month
      existingMonths[monthIndex] = {
        ...existingMonths[monthIndex],
        ...monthData,
        amount: monthData.amount || existingMonths[monthIndex].amount
      };
    } else {
      // Add new month (insert in chronological order)
      existingMonths.push({
        year: monthData.year,
        month: monthData.month,
        amount: monthData.amount || 0,
        point: 0, // Will be recalculated
        created_at: monthData.created_at || serverTimestamp(),
        // NO individual_percentage - only use amount field
        doc_id: monthData.doc_id || ""
      });
      
      // Sort chronologically
      existingMonths.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });
    }
    
    // Recalculate all points
    const monthsWithRecalculatedPoints = recalculateAllPoints(existingMonths);
    
    // Validate point calculation
    monthsWithRecalculatedPoints.forEach((month, index) => {
      let expectedPoint = 0;
      for (let i = 0; i <= index; i++) {
        expectedPoint += monthsWithRecalculatedPoints[i].amount || 0;
      }
      
      if (Math.abs(month.point - expectedPoint) > 0.0001) {
        console.warn(`⚠️ Point validation failed for ${month.year}-${month.month}: expected ${expectedPoint}, got ${month.point}`);
      }
    });
    
    // Update consolidated document
    await setDoc(summaryDocRef, {
      months: monthsWithRecalculatedPoints,
      lastUpdated: serverTimestamp(),
      version: 1
    }, { merge: true });
    
    console.log(`✅ Updated consolidated document: ${monthsWithRecalculatedPoints.length} months`);
    return monthsWithRecalculatedPoints;
  } catch (error) {
    console.error("❌ Error updating yearly graph summary:", error);
    throw error;
  }
};

/**
 * Remove month from consolidated document and recalculate subsequent points
 */
export const removeMonthFromSummary = async (year, month) => {
  try {
    const summaryDocRef = doc(DATABASE, CONSOLIDATED_DOC_PATH);
    const summaryDocSnap = await getDoc(summaryDocRef);
    
    if (!summaryDocSnap.exists()) {
      console.warn("⚠️ Consolidated document doesn't exist, nothing to remove");
      return;
    }
    
    let existingMonths = summaryDocSnap.data().months || [];
    
    // Remove the month
    const filteredMonths = existingMonths.filter(
      m => !(m.year === year && m.month === month)
    );
    
    if (filteredMonths.length === existingMonths.length) {
      console.warn(`⚠️ Month ${year}-${month} not found in consolidated document`);
      return;
    }
    
    // Recalculate all points after removal
    const monthsWithRecalculatedPoints = recalculateAllPoints(filteredMonths);
    
    // Update consolidated document
    await setDoc(summaryDocRef, {
      months: monthsWithRecalculatedPoints,
      lastUpdated: serverTimestamp(),
      version: 1
    }, { merge: true });
    
    console.log(`✅ Removed month ${year}-${month} and recalculated ${monthsWithRecalculatedPoints.length} months`);
    return monthsWithRecalculatedPoints;
  } catch (error) {
    console.error("❌ Error removing month from summary:", error);
    throw error;
  }
};

/**
 * Recalculate point field for all months (validation/repair function)
 * Returns validation report
 */
export const recalculateAllPointsInSummary = async () => {
  try {
    const summaryDocRef = doc(DATABASE, CONSOLIDATED_DOC_PATH);
    const summaryDocSnap = await getDoc(summaryDocRef);
    
    if (!summaryDocSnap.exists()) {
      console.warn("⚠️ Consolidated document doesn't exist");
      return { success: false, error: "Document doesn't exist" };
    }
    
    let existingMonths = summaryDocSnap.data().months || [];
    
    // Recalculate all points
    const monthsWithRecalculatedPoints = recalculateAllPoints(existingMonths);
    
    // Validate point calculation
    const validationReport = {
      totalMonths: monthsWithRecalculatedPoints.length,
      correctedMonths: [],
      errors: []
    };
    
    monthsWithRecalculatedPoints.forEach((month, index) => {
      let expectedPoint = 0;
      for (let i = 0; i <= index; i++) {
        expectedPoint += monthsWithRecalculatedPoints[i].amount || 0;
      }
      
      const difference = Math.abs(month.point - expectedPoint);
      if (difference > 0.0001) {
        validationReport.correctedMonths.push({
          year: month.year,
          month: month.month,
          oldPoint: month.point,
          newPoint: expectedPoint,
          difference
        });
        month.point = expectedPoint;
      }
    });
    
    // Update consolidated document if corrections were made
    if (validationReport.correctedMonths.length > 0) {
      await setDoc(summaryDocRef, {
        months: monthsWithRecalculatedPoints,
        lastUpdated: serverTimestamp(),
        version: 1
      }, { merge: true });
      console.log(`✅ Recalculated and corrected ${validationReport.correctedMonths.length} months`);
    } else {
      console.log("✅ All points are correct");
    }
    
    return { success: true, report: validationReport };
  } catch (error) {
    console.error("❌ Error recalculating points:", error);
    return { success: false, error: error.message };
  }
};

